import {useEffect, useRef, useMemo} from 'react';
import {Chart} from "primereact/chart";
import {generatePath, useNavigate} from "react-router-dom";
import moment from "moment";

type Props = {
    sellers:      any[];   // users (type Seller) — for resolving names
    stats:        any[];   // sellerStats docs: { id, daily: {'YYYY-MM-DD': {views,...}}, views, ... }
    toggleNumber: number;
};

const MONTHS = Array.from({length: 6}, (_, i) =>
    moment().subtract(i, 'months').format('MMMM')
);

function sum(arr: number[]) { return arr.reduce((a, b) => a + b, 0); }

function randomColor() {
    let c = '#';
    for (let i = 0; i < 3; i++)
        c += ('1' + Math.floor(((1 + Math.random()) * Math.pow(16, 2)) / 2).toString(16)).slice(-2);
    return c;
}

// Monthly view counts from the per-day buckets on a stats doc.
function monthlyViews(daily: any): number[] {
    return MONTHS.map(month =>
        Object.entries(daily ?? {}).reduce((acc: number, [date, counts]: [string, any]) =>
            moment(date, 'YYYY-MM-DD').format('MMMM') === month
                ? acc + (Number(counts?.views) || 0)
                : acc, 0)
    );
}

const TopViewedSellers = ({stats, sellers, toggleNumber}: Props) => {
    const navigate  = useNavigate();
    const chartRef  = useRef<any>(null);
    useEffect(() => () => { try { chartRef.current?.getChart?.()?.destroy(); } catch(_){} }, []);

    const chartData = useMemo(() => {
        const datasets = stats.map(stat => {
            const name = sellers.find(s => s.Uid === stat.id || s.sellerID === stat.id)?.fullName ?? stat.id;
            return {
                label: name,
                seller: stat.id,
                data: monthlyViews(stat.daily),
                fill: false,
                borderColor: randomColor(),
                tension: .4,
            };
        });
        return {
            labels:   MONTHS,
            datasets: datasets.sort((a, b) => sum(b.data) - sum(a.data)).slice(0, toggleNumber),
        };
    }, [stats, sellers, toggleNumber]);

    const options = useMemo(() => ({
        maintainAspectRatio: false,
        aspectRatio: .6,
        plugins: {
            legend: {
                labels: {color: '#495057'},
                onClick: (_e: any, item: any) => {
                    const seller = chartData.datasets[item.datasetIndex]?.seller;
                    if (seller) navigate(generatePath('/shops/:id', {id: seller}));
                },
            },
        },
        scales: {
            x: {ticks: {color: '#495057'}, grid: {color: '#ebedef'}},
            y: {ticks: {color: '#495057'}, grid: {color: '#ebedef'}},
        },
    }), [chartData, navigate]);

    return (
        <div style={{position:'relative', height:240}}>
            <Chart ref={chartRef} type="line" data={chartData} options={options}/>
        </div>
    );
};

export default TopViewedSellers;
